import React, { Dispatch, SetStateAction, useState } from 'react';

import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import { apiCheckout } from 'src/environments/Checkout/api';
import { ICustomerProductData } from 'src/environments/Checkout/interfaces';

import createPagarmeCardToken from 'src/utils/createPagarmeCardToken';
import InputCheckout from 'src/components/functional/InputCheckout';
import { BackArrowIcon } from 'src/components/style/BackArrowIcon';
import SelectExpirationDate from './components/SelectExpirationDate';

import {
  BackButton,
  ButtonContinue,
  ButtonPayment,
  ContainerCreditCardForm,
  CreditCardIcon,
  LoadingGif,
  SideBySide,
  TitlePayment,
} from './style';

import whiteCreditCardIconSVG from '../../../../../../../../../../assets/images/subscripiton-checkout-icons/white-credit-card-icon.svg';
import whiteArrowIconSVG from '../../../../../../../../../../assets/images/subscripiton-checkout-icons/white-arrow-left.svg';
import loadingGIF from '../../../../../../../../../../assets/gifs/loading.png';

type Props = {
  customerData: ICustomerProductData;
  hasCards: boolean;
  setShowCardForm: Dispatch<SetStateAction<boolean>>;
  setShowPaymentMethod: Dispatch<SetStateAction<string>>;
};

export default function CreditCardForm({
  customerData,
  hasCards,
  setShowCardForm,
  setShowPaymentMethod,
}: Props): JSX.Element {
  const [cardData, setCardData] = useState({
    cardHolderName: '',
    cardNumber: '',
    expMonth: '',
    expYear: '',
    cardCvv: '',
    amount: 0,
    installments: 1,
  });
  const [isDisabled, setIsDisabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const queryClient = useQueryClient();

  const { token } = useParams();

  const onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = event.target;
    setCardData({
      ...cardData,
      [name]: value,
    });
  };

  const { mutate: createCardIdMutate } = useMutation(
    async ({ customerDataObj, cardToken }: any) =>
      apiCheckout.createCustomerAndCardId(customerDataObj, cardToken, token),
    {
      onError: () => {
        void Swal.fire({
          icon: 'error',
          title: 'Cartão inválido ou bloqueado!',
          text: 'Por favor, confira os dados do seu cartão!',
          iconColor: 'black',
          confirmButtonColor: 'black',
        });
        setLoading(false);
        setIsDisabled(false);
      },
      onSuccess: async () => {
        setShowCardForm(false);
        setLoading(false);
        setIsDisabled(false);
        await queryClient.invalidateQueries('getCardsIds');
        void Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: 'Meio de pagamento salvo!',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    }
  );

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setIsDisabled(true);
    setLoading(true);

    const paymentCardData = {
      ...cardData,
      cardNumber: cardData.cardNumber.replaceAll(' ', ''),
    };

    const customerDataObj = {
      ...customerData,
      customerPhone: customerData.customerPhone.replace(/[^a-zA-Z0-9]/g, '').replace(' ', ''),
      customerCpf: customerData.customerCpf.replace(/[^a-zA-Z0-9]/g, ''),
      zipCode: customerData.zipCode.replace('-', ''),
    };

    const cardToken = await createPagarmeCardToken(paymentCardData);

    if (cardToken === 'Error') {
      setLoading(false);
      setIsDisabled(false);
      return;
    }

    createCardIdMutate({ customerDataObj, cardToken });
  };

  return (
    <ContainerCreditCardForm onSubmit={handleSubmitForm}>
      {hasCards && (
        <BackArrowIcon
          src={whiteArrowIconSVG}
          onClick={() => {
            setShowCardForm(false);
          }}
        />
      )}
      <TitlePayment>Pagamento</TitlePayment>
      <ButtonPayment>
        <CreditCardIcon src={whiteCreditCardIconSVG} alt="Ícone de cartão de crédito" />
        <p>Cartão de Crédito</p>
        <BackButton
          type="button"
          onClick={() => {
            if (hasCards) {
              setShowCardForm(false);
            } else {
              setShowPaymentMethod('');
            }
          }}
        >
          Voltar
        </BackButton>
      </ButtonPayment>
      <InputCheckout
        label="Número do Cartão"
        name="cardNumber"
        mask="9999 9999 9999 9999"
        inputMode="numeric"
        value={cardData.cardNumber}
        inputWidth="89.333vw"
        onChange={onChange}
        placeholder="0000 0000 0000 0000"
        colorBorder={cardData.cardNumber !== '' ? '#000000' : '#c7c7c7'}
      />
      <InputCheckout
        label="Nome no Cartão"
        inputWidth="89.333vw"
        name="cardHolderName"
        value={cardData.cardHolderName}
        placeholder="Nome e Sobrenome"
        maxLength={42}
        onChange={onChange}
        colorBorder={cardData.cardHolderName !== '' ? '#000000' : '#c7c7c7'}
      />
      <SideBySide>
        <SelectExpirationDate onChange={onChange} />
        <InputCheckout
          label="CVV"
          inputWidth="30.666vw"
          name="cardCvv"
          placeholder="000"
          value={cardData.cardCvv}
          maxLength={4}
          onChange={onChange}
          inputMode="numeric"
          colorBorder={cardData.cardCvv !== '' ? '#000000' : '#c7c7c7'}
        />
      </SideBySide>
      <ButtonContinue type="submit" disabled={isDisabled}>
        {loading ? <LoadingGif src={loadingGIF} alt="gif de carregamento" /> : 'SALVAR'}
      </ButtonContinue>
    </ContainerCreditCardForm>
  );
}
